import React, { useState, useEffect, useRef } from "react"
import Modal, { Action } from "@kit/design/modal"
import { toast } from "@kit/design/toast"
import { OldButton } from "@kit/design/old-button"
import cx from "classnames"
import { useCurrentNode } from "../../index"
import { useRenderContext } from "../../render-context"
import { HTMLEditor, scopeStyles } from "core/html-editor"
import SlateNode from "../slate-node"
import { sanitizeHTML, defaultConfig } from "utils/sanitize-html"

function useAutoFocus() {
  const ref = useRef()

  useEffect(() => {
    setTimeout(() => {
      ref.current.focus()
    })
  }, [])

  return ref
}

function useConvertLegacyHTMLFormat({ editor, node }) {
  useEffect(() => {
    const codeNode = node.nodes.first()
    if (codeNode && codeNode.nodes) {
      const html = codeNode.nodes
        .map(node =>
          node
            .getTexts()
            .toArray()
            .map(t => t.text)
            .join("")
        )
        .join("\n")

      // The setTimeout works around slate weirdness
      setTimeout(() => {
        editor.setNodeByKey(node.key, { data: { html } })
        editor.removeNodeByKey(codeNode.key)
      })
    }
  }, [])
}

function EditHTMLModal({ onSave, onCancel, initialHTML }) {
  const [HTML, setHTML] = useState(initialHTML)
  const [HTMLError, setHTMLError] = useState("")
  const ref = useAutoFocus()

  const handleChange = ({ value, error }) => {
    setHTMLError(error)
    setHTML(value)
  }

  const handleSave = () => {
    if (HTMLError) {
      toast({ type: "error", text: HTMLError })
    } else {
      onSave(HTML)
    }
  }
  return (
    <Modal label="Edit HTML" onClose={onCancel}>
      <HTMLEditor
        className="w-full h-64"
        ref={ref}
        onChange={handleChange}
        value={HTML}
      />
      <Action is="secondary" onClick={onCancel}>
        Cancel
      </Action>
      <Action is="primary" onClick={handleSave}>
        Save
      </Action>
    </Modal>
  )
}

const HTMLNode = ({ node, attributes, editor }) => {
  const [active, setActive] = useState(false)
  const focusBlock = useCurrentNode()
  const focused =
    focusBlock && (focusBlock === node || node.hasDescendant(focusBlock.key))
  const { target, mode } = useRenderContext()

  const handleSaveHTML = html => {
    editor.setNodeByKey(node.key, { data: { html } })
    setActive(false)
  }

  useConvertLegacyHTMLFormat({ editor, node })

  const htmlBlock = scopeStyles(
    node.data.get("html"),
    ["email-container", "formkit-page"],
    false
  )

  const sanitized = sanitizeHTML(htmlBlock, {
    ...defaultConfig,
    allowedTags: defaultConfig.allowedTags.concat(["style"]),
    allowVulnerableTags: true
  })

  const htmlContainerClass = cx(
    "p-2 text-gray-600 border border-gray-300/0 whitespace-normal",
    {
      "bg-gray-100 border-gray-300/100": focused,
      "hover:border-gray-300/100 group-hover:bg-gray-100": mode === "editing"
    }
  )
  const editBtnClass = cx(
    "grid place-content-center h-6 opacity-0 group-hover:opacity-100 justify-self-end",
    {
      "opacity-100": focused
    }
  )
  return (
    <SlateNode node={node} className="grid relative group" {...attributes}>
      <div
        className={htmlContainerClass}
        style={{ gridArea: "1/1/2/4" }}
        data-live={mode === "live"}
        dangerouslySetInnerHTML={{
          __html: sanitized || "<div>Enter HTML here</div>"
        }}
      ></div>
      {target === "web" && mode === "live" ? null : (
        <OldButton
          type="button"
          size="sm"
          is="primary"
          contentEditable={false}
          className={editBtnClass}
          style={{ gridArea: "1 / 3 / 2 / 4" }}
          onClick={e => {
            // prevents calling onClick on the entire node that would result in
            // an endless re-render loop
            e.stopPropagation()
            setActive(true)
          }}
        >
          Edit
        </OldButton>
      )}
      {active && (
        <EditHTMLModal
          onCancel={() => setActive(false)}
          onSave={handleSaveHTML}
          initialHTML={node.data.get("html")}
        />
      )}
    </SlateNode>
  )
}

export default HTMLNode
